import type {
  TransformationGraphEdge,
  TransformationGraphNode,
} from "@shared/types/transformations";

/**
 * Where each node of the model graph sits, laid out by depth (canvas 16).
 *
 * No graph library: the graph is a dbt project's models, which is a DAG of a
 * few dozen nodes at most, and depth is all that has to be right for it to be
 * readable. depth = 0 for a node with no parent in the graph (sources, and a
 * model that selects from nothing the manifest lists), else 1 + the deepest
 * parent. Columns are 250 px apart, rows 96 px; edges are cubic curves from the
 * parent's right edge to the child's left edge, so a node reads left to right
 * as "what it selects from → what selects from it".
 *
 * Pure: it takes the API's nodes and edges and answers coordinates, which is
 * what makes it testable without a DOM.
 */

export const NODE_WIDTH = 208;
export const NODE_HEIGHT = 66;
export const COLUMN_GAP = 250;
export const ROW_GAP = 96;
export const ORIGIN_X = 24;
export const ORIGIN_Y = 34;

export interface LaidOutNode {
  node: TransformationGraphNode;
  depth: number;
  x: number;
  y: number;
}

export interface LaidOutEdge {
  edge: TransformationGraphEdge;
  /** The SVG path from the dependency's right edge to the dependent's left edge. */
  path: string;
}

export interface GraphColumn {
  depth: number;
  label: string;
  x: number;
}

export interface GraphLayout {
  nodes: LaidOutNode[];
  edges: LaidOutEdge[];
  columns: GraphColumn[];
  width: number;
  height: number;
}

/**
 * depth = 1 + max(parent depth); sources at 0. A back edge — which a dbt
 * manifest cannot carry, but a hand-written fixture can — is ignored rather
 * than recursed into, so the layout terminates on any input.
 */
function depthsOf(nodes: TransformationGraphNode[]): Map<string, number> {
  const byId = new Map(nodes.map((node) => [node.uniqueId, node]));
  const depth = new Map<string, number>();
  const visiting = new Set<string>();

  const depthOf = (id: string): number => {
    const known = depth.get(id);
    if (known !== undefined) return known;
    if (visiting.has(id)) return 0;
    visiting.add(id);
    const parents = (byId.get(id)?.dependsOn ?? []).filter((parent) => byId.has(parent));
    const value = parents.length === 0 ? 0 : 1 + Math.max(...parents.map(depthOf));
    visiting.delete(id);
    depth.set(id, value);
    return value;
  };

  for (const node of nodes) depthOf(node.uniqueId);
  return depth;
}

export function layoutGraph(
  nodes: TransformationGraphNode[],
  edges: TransformationGraphEdge[],
): GraphLayout {
  if (nodes.length === 0) {
    return { nodes: [], edges: [], columns: [], width: 0, height: 0 };
  }

  const depth = depthsOf(nodes);
  const columns = new Map<number, TransformationGraphNode[]>();
  for (const node of nodes) {
    const d = depth.get(node.uniqueId) ?? 0;
    columns.set(d, [...(columns.get(d) ?? []), node]);
  }
  const depths = [...columns.keys()].sort((a, b) => a - b);
  const tallest = Math.max(...depths.map((d) => (columns.get(d) ?? []).length));

  const positions = new Map<string, { x: number; y: number }>();
  const laidOut: LaidOutNode[] = [];
  for (const d of depths) {
    const items = [...(columns.get(d) ?? [])].sort((a, b) => a.name.localeCompare(b.name));
    // Shorter columns are centred on the tallest one, so a two-source column
    // sits level with a four-model one rather than hanging from the top.
    const top = ORIGIN_Y + ((tallest - items.length) * ROW_GAP) / 2;
    items.forEach((node, index) => {
      const at = { x: ORIGIN_X + d * COLUMN_GAP, y: top + index * ROW_GAP };
      positions.set(node.uniqueId, at);
      laidOut.push({ node, depth: d, x: at.x, y: at.y });
    });
  }

  const laidOutEdges: LaidOutEdge[] = [];
  for (const edge of edges) {
    const from = positions.get(edge.from);
    const to = positions.get(edge.to);
    if (!from || !to) continue;
    const startX = from.x + NODE_WIDTH;
    const startY = from.y + NODE_HEIGHT / 2;
    const endX = to.x;
    const endY = to.y + NODE_HEIGHT / 2;
    const middle = (startX + endX) / 2;
    laidOutEdges.push({
      edge,
      path: `M${startX},${startY} C${middle},${startY} ${middle},${endY} ${endX},${endY}`,
    });
  }

  const columnLabels: GraphColumn[] = depths.map((d) => {
    const items = columns.get(d) ?? [];
    const allSources = items.every((node) => node.resourceType === "source");
    return {
      depth: d,
      label: d === 0 && allSources ? "Sources" : `Depth ${d}`,
      x: ORIGIN_X + d * COLUMN_GAP,
    };
  });

  return {
    nodes: laidOut,
    edges: laidOutEdges,
    columns: columnLabels,
    width: ORIGIN_X + ((depths[depths.length - 1] ?? 0) + 1) * COLUMN_GAP,
    height: ORIGIN_Y + tallest * ROW_GAP + 8,
  };
}
